import { formatCurrency } from '@eriko/shared-types';
import { ArrowDownRight, ArrowRight, ArrowUpRight, GitCompare, Minus } from 'lucide-react';

interface Row {
  name: string;
  period1Value: number;
  period2Value: number;
  absChange: number;
  pctChange: number | null;
}

interface Summary {
  totalPeriod1: number;
  totalPeriod2: number;
  totalChange: number;
  totalChangePct: number | null;
}

interface Data {
  type: string;
  reportType: 'pnl' | 'balance_sheet' | 'expenses';
  currency?: string;
  period1Label: string;
  period2Label: string;
  rows: Row[];
  summary: Summary;
}

const titleFor = (t: Data['reportType']) =>
  t === 'pnl'
    ? 'P&L Comparison'
    : t === 'balance_sheet'
      ? 'Balance Sheet Comparison'
      : 'Expense Comparison';

const rowHeaderFor = (t: Data['reportType']) => (t === 'balance_sheet' ? 'Account' : 'Line item');

const ISO_RANGE_RE = /^(\d{4})-(\d{2})-(\d{2}) to (\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatPeriodLabel(label: string): string {
  const m = label.match(ISO_RANGE_RE);
  if (!m) return label;
  const [, y1, mo1, d1, y2, mo2, d2] = m;
  const isFullYear = y1 === y2 && mo1 === '01' && d1 === '01' && mo2 === '12' && d2 === '31';
  if (isFullYear) return y1!;
  const start = `${MONTHS[Number(mo1) - 1] ?? ''} ${Number(d1)}`;
  const end = `${MONTHS[Number(mo2) - 1] ?? ''} ${Number(d2)}`;
  return y1 === y2 ? `${start} – ${end}, ${y1}` : `${start}, ${y1} – ${end}, ${y2}`;
}

const changeTone = (v: number | null) =>
  v == null || v === 0 ? 'text-surface-400' : v > 0 ? 'text-pos' : 'text-neg';

const mutedIfZero = (v: number) => (v === 0 ? 'text-surface-500' : 'text-surface-700');

const signedPct = (v: number | null) => {
  if (v == null) return 'new';
  if (v === 0) return '0%';
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
};

const signedDollar = (v: number, currency: string) => {
  if (v === 0) return formatCurrency(0, 2, currency);
  return `${v > 0 ? '+' : ''}${formatCurrency(v, 2, currency)}`;
};

function DeltaIcon({ value }: { value: number | null }) {
  if (value == null || value === 0) {
    return <Minus className="h-3 w-3 shrink-0" strokeWidth={2} />;
  }
  return value > 0 ? (
    <ArrowUpRight className="h-3 w-3 shrink-0" strokeWidth={2} />
  ) : (
    <ArrowDownRight className="h-3 w-3 shrink-0" strokeWidth={2} />
  );
}

export function ReportComparisonReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const period1 = formatPeriodLabel(d.period1Label);
  const period2 = formatPeriodLabel(d.period2Label);
  const currency = d.currency ?? 'USD'; // home currency for correct symbol (CUR-1)

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between gap-3 border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <GitCompare className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">{titleFor(d.reportType)}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
          <span className="tabular-nums text-surface-700">{period1}</span>
          <ArrowRight className="h-3 w-3 text-surface-600" strokeWidth={2} aria-hidden />

          <span className="tabular-nums text-surface-700">{period2}</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-50 text-[12px] uppercase tracking-[0.04em] text-surface-500">
              <th className="px-4.5 py-2 text-left font-medium">{rowHeaderFor(d.reportType)}</th>
              <th className="px-3 py-2 text-right font-medium">
                <span className="block leading-tight">{period1}</span>
              </th>
              <th className="px-3 py-2 text-right font-medium">
                <span className="block leading-tight">{period2}</span>
              </th>
              <th className="px-3 py-2 text-right font-medium">Δ $</th>
              <th className="px-4.5 py-2 text-right font-medium">Δ %</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={i} className="border-t border-surface-50 hover:bg-surface-50">
                <td className="truncate px-4.5 py-1.75 text-surface-700">{r.name}</td>
                <td
                  className={`px-3 py-1.75 text-right font-mono tabular-nums ${mutedIfZero(r.period1Value)}`}
                >
                  {formatCurrency(r.period1Value, 2, currency)}
                </td>
                <td
                  className={`px-3 py-1.75 text-right font-mono tabular-nums ${mutedIfZero(r.period2Value)}`}
                >
                  {formatCurrency(r.period2Value, 2, currency)}
                </td>
                <td
                  className={`px-3 py-1.75 text-right font-mono tabular-nums ${changeTone(r.absChange)}`}
                >
                  <span className="inline-flex items-center justify-end gap-1">
                    <DeltaIcon value={r.absChange} />
                    <span>{signedDollar(r.absChange, currency)}</span>
                  </span>
                </td>
                <td
                  className={`px-4.5 py-1.75 text-right font-mono tabular-nums ${changeTone(r.pctChange)}`}
                >
                  {signedPct(r.pctChange)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-surface-200 bg-surface-50 font-semibold">
              <td className="px-4.5 py-2 text-surface-900">Total</td>
              <td
                className={`px-3 py-2 text-right font-mono tabular-nums ${d.summary.totalPeriod1 === 0 ? 'text-surface-500' : 'text-surface-900'}`}
              >
                {formatCurrency(d.summary.totalPeriod1, 2, currency)}
              </td>
              <td
                className={`px-3 py-2 text-right font-mono tabular-nums ${d.summary.totalPeriod2 === 0 ? 'text-surface-400' : 'text-surface-900'}`}
              >
                {formatCurrency(d.summary.totalPeriod2, 2, currency)}
              </td>
              <td
                className={`px-3 py-2 text-right font-mono tabular-nums ${changeTone(d.summary.totalChange)}`}
              >
                <span className="inline-flex items-center justify-end gap-1">
                  <DeltaIcon value={d.summary.totalChange} />
                  <span>{signedDollar(d.summary.totalChange, currency)}</span>
                </span>
              </td>
              <td
                className={`px-4.5 py-2 text-right font-mono tabular-nums ${changeTone(d.summary.totalChangePct)}`}
              >
                {signedPct(d.summary.totalChangePct)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
